// T18/T19/T20 — Admin console. Route is guarded by the admin claim.
// Master switches, broadcasts, guest management (+ reset binding), and quick
// add/edit of dossier cases.
import {
  collection, doc, getDoc, getDocs, onSnapshot, setDoc, updateDoc, deleteDoc,
  serverTimestamp, Timestamp, query, orderBy,
} from "firebase/firestore";
import { ref, deleteObject, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, isAdmin, signInWithGoogle, signOut, currentUser } from "../lib/firebase";
import { compressImage, uploadSelfie } from "../lib/upload";
import { surveillanceFilter } from "../lib/avatar-filter";
import { el, mount, toast } from "../lib/dom";
import { go } from "../router";
import { sendBroadcast, adminResetBinding, generateAvatar } from "../lib/data";
import { spyMap, blipIcon, pinIcon, roughZoneFor, type SpyMap, GUN_SVG, SUPPLY_SVG } from "../lib/map";
import { icon } from "../components/icons";

const spySilhouette = `<svg viewBox="0 0 60 80" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%;padding:10px;opacity:0.3"><ellipse cx="30" cy="22" rx="13" ry="15" fill="var(--amber)"/><path d="M4 80c0-18 12-30 26-30s26 12 26 30H4Z" fill="var(--amber)"/></svg>`;

export async function renderAdmin(): Promise<void> {
  // Admin gate: only the authorized Google account (or admin claim) gets in.
  if (!(await isAdmin())) return renderGate();

  const root = el("div");
  mount(root);

  const header = el("div.row", { style: "justify-content:space-between" },
    el("h1.amber", {}, "CONTROL"),
    el("button.btn--ghost.btn.btn--sm", {
      onclick: async () => { await signOut(); go("/admin"); },
    }, "Sign out"),
  );

  // Tabbed console. Each tab builds lazily on first open and is cached; the
  // ops map lives under "Control" and re-fits itself on show via a resize event.
  type Tab = { id: string; ico: string; label: string; build: () => Promise<HTMLElement> | HTMLElement };
  const tabs: Tab[] = [
    { id: "control", ico: "control", label: "Control", build: () => controlTab() },
    { id: "agents", ico: "agent", label: "Agents", build: () => guestsSection() },
    { id: "caches", ico: "caches", label: "Caches", build: () => cachesSection() },
    { id: "cases", ico: "cases", label: "Cases", build: () => casesSection() },
    { id: "proofs", ico: "proof", label: "Proofs", build: () => proofsSection() },
  ];

  const content = el("div");
  const cache = new Map<string, HTMLElement>();
  const tabBtns = new Map<string, HTMLElement>();

  async function activate(id: string) {
    for (const [tid, btn] of tabBtns) btn.classList.toggle("tab--active", tid === id);
    const tab = tabs.find((t) => t.id === id)!;
    if (!cache.has(id)) {
      const loading = el("p.tiny.dim", {}, "Loading…");
      content.replaceChildren(loading);
      cache.set(id, await tab.build());
    }
    content.replaceChildren(cache.get(id)!);
    // Let Leaflet (Control tab) recompute size now that it's visible.
    window.dispatchEvent(new Event("resize"));
  }

  const tabBar = el("div.tabbar", {},
    ...tabs.map((t) => {
      const b = el("button.tab", { onclick: () => void activate(t.id) }, icon(t.ico, 16), t.label);
      tabBtns.set(t.id, b);
      return b;
    }),
  );

  switchToControl = () => activate("control");

  root.replaceChildren(header, tabBar, content);
  await activate("control");
}

/** Control tab: master switches + broadcasts + live ops map. */
async function controlTab(): Promise<HTMLElement> {
  return el("div", { style: "display:grid;grid-template-columns:repeat(auto-fit,minmax(340px,1fr));gap:14px;align-items:start" },
    switchesSection(),
    broadcastSection(),
    await positionsSection(),
  );
}

// ---- shared: live countdown + manual-release toggle ----
function countdown(releaseAt?: { toMillis(): number } | null): HTMLElement {
  const span = el("span.tiny", {});
  function tick() {
    const t = releaseAt?.toMillis?.();
    if (!t) { span.textContent = "no release time"; span.className = "tiny dim"; return; }
    const ms = t - Date.now();
    if (ms <= 0) { span.textContent = "⏱ released (time reached)"; span.className = "tiny dim"; return; }
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    span.textContent = `⏱ releases in ${d > 0 ? d + "d " : ""}${h}h ${m}m`;
    span.className = "tiny amber";
  }
  tick();
  setInterval(tick, 30000);
  return span;
}

/** A checkbox that force-releases a case immediately (sets released). */
function releaseToggle(coll: string, id: string, released: boolean): HTMLElement {
  const cb = el("input", { type: "checkbox" }) as HTMLInputElement;
  cb.checked = released;
  cb.addEventListener("change", async () => {
    try {
      await setDoc(doc(db, coll, id), { released: cb.checked }, { merge: true });
      toast(cb.checked ? "Released now." : "Release revoked.", "ok");
    } catch (e: unknown) {
      toast((e as { message?: string })?.message ?? "Failed", "err");
      cb.checked = !cb.checked;
    }
  });
  return el("label.row", { style: "gap:8px" }, cb, el("span.tiny", {}, "Release now"));
}

/** Google sign-in gate shown when the visitor isn't the authorized admin. */
function renderGate(): void {
  const btn = el("button.btn", {}, "🔓 Sign in with Google") as HTMLButtonElement;
  const msg = el("p.tiny.dim.center", { style: "margin-top:12px" }, "Authorized personnel only.");
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    try {
      await signInWithGoogle();
      if (await isAdmin()) { toast("Welcome back, CONTROL.", "ok"); renderAdmin(); }
      else { toast("This account is not authorized.", "err"); await signOut(); btn.disabled = false; }
    } catch (e: unknown) {
      toast((e as { message?: string })?.message ?? "Sign-in failed.", "err");
      btn.disabled = false;
    }
  });
  mount(el("div", {},
    el("div.center", {}, el("h1.amber", {}, "CONTROL"), el("p.tiny.dim", {}, "HANDLER ACCESS ONLY")),
    el("div.card", {}, el("span.card__tag", {}, "CLEARANCE REQUIRED"),
      el("p", {}, "Authenticate to reach the operations console."), btn, msg),
    el("button.btn--ghost.btn", { style: "margin-top:10px", onclick: () => go("/activate") }, "← Back to agent login"),
  ));
}

// ---- live ops map: guest positions + cache coordinates ----
// Editors call armPicker() to let CONTROL tap the map and capture a coord.
let pickCb: ((lat: number, lng: number) => void) | null = null;
const pickerStatus = el("p.tiny.dim", {}, "");
// Set by renderAdmin so armPicker can auto-switch to the Control tab.
let switchToControl: (() => void) | null = null;

export function armPicker(label: string, cb: (lat: number, lng: number) => void): void {
  pickCb = cb;
  pickerStatus.textContent = `🎯 Tap the map to set: ${label}`;
  pickerStatus.classList.add("amber");
  switchToControl?.();
  toast(`Switch to Control tab, then tap the map to place: ${label}`, "ok");
}

async function positionsSection(): Promise<HTMLElement> {
  const mapEl = el("div", { style: "height:380px" });
  const drawBtn = el("button.btn--ghost.btn.btn--sm", {}, "✏️ Draw area") as HTMLButtonElement;
  const saveBtn = el("button.btn.btn--sm", { style: "display:none" }, "💾 Save area") as HTMLButtonElement;
  const cancelBtn = el("button.btn--ghost.btn.btn--sm", { style: "display:none" }, "✕ Cancel") as HTMLButtonElement;
  const clearBtn = el("button.btn--ghost.btn.btn--sm", {}, "🗑 Clear area") as HTMLButtonElement;
  const box = el("div.card", {},
    el("span.card__tag", {}, "OPS MAP — LIVE POSITIONS & SITES"),
    mapEl,
    el("div.row", { style: "gap:6px;margin-top:8px;flex-wrap:wrap" }, drawBtn, saveBtn, cancelBtn, clearBtn),
    pickerStatus,
  );

  void (async () => {
    const sm: SpyMap = await spyMap(mapEl, { zoom: 14 });

    // ---- game-area polygon: display + click-to-draw editor ----
    let areaPoly: ReturnType<SpyMap["L"]["polygon"]> | null = null;
    let drawing = false;
    let draft: Array<{ lat: number; lng: number }> = [];

    function showArea(points: Array<{ lat: number; lng: number }>, style: "saved" | "draft") {
      areaPoly?.remove();
      areaPoly = null;
      if (points.length < 2) return;
      areaPoly = sm.L.polygon(points.map((p) => [p.lat, p.lng] as [number, number]), {
        color: style === "saved" ? "var(--green)" : "var(--amber)",
        weight: 1.5,
        dashArray: style === "draft" ? "4 4" : undefined,
        fillColor: style === "saved" ? "var(--green)" : "var(--amber)",
        fillOpacity: 0.05,
      }).addTo(sm.map);
      if (style === "saved") sm.map.fitBounds(areaPoly.getBounds(), { padding: [24, 24] });
    }

    const savedArea = (await getDoc(doc(db, "settings", "app"))).data()?.gameArea;
    if (savedArea?.points?.length >= 3) showArea(savedArea.points, "saved");
    // The Control tab content is swapped in after build, so the container had
    // zero size when Leaflet measured it — re-measure and re-frame once shown.
    setTimeout(() => {
      sm.map.invalidateSize();
      if (areaPoly) sm.map.fitBounds(areaPoly.getBounds(), { padding: [24, 24] });
    }, 350);

    function setDrawing(on: boolean) {
      drawing = on;
      drawBtn.style.display = on ? "none" : "";
      saveBtn.style.display = on ? "" : "none";
      cancelBtn.style.display = on ? "" : "none";
      pickerStatus.textContent = on ? "✏️ Tap the map to add corners of the play area." : "";
      pickerStatus.classList.toggle("amber", on);
    }
    drawBtn.addEventListener("click", () => { draft = []; setDrawing(true); });
    cancelBtn.addEventListener("click", async () => {
      setDrawing(false);
      areaPoly?.remove(); areaPoly = null;
      const saved = (await getDoc(doc(db, "settings", "app"))).data()?.gameArea;
      if (saved?.points?.length >= 3) showArea(saved.points, "saved");
    });
    saveBtn.addEventListener("click", async () => {
      if (draft.length < 3) { toast("Need at least 3 corners.", "warn"); return; }
      await setDoc(doc(db, "settings", "app"), { gameArea: { points: draft } }, { merge: true });
      setDrawing(false);
      showArea(draft, "saved");
      toast("Operation zone saved. Guest maps will zoom to it.", "ok");
    });
    clearBtn.addEventListener("click", async () => {
      if (!confirm("Remove the operation zone?")) return;
      await setDoc(doc(db, "settings", "app"), { gameArea: null }, { merge: true });
      areaPoly?.remove(); areaPoly = null;
      toast("Operation zone cleared.", "ok");
    });

    sm.map.on("click", (e) => {
      const { lat, lng } = e.latlng;
      if (drawing) {
        draft.push({ lat, lng });
        showArea(draft, "draft");
        pickerStatus.textContent = `✏️ ${draft.length} corner${draft.length > 1 ? "s" : ""} — Save when done.`;
        return;
      }
      if (!pickCb) return;
      pickCb(lat, lng);
      pickCb = null;
      pickerStatus.textContent = `Set: ${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      pickerStatus.classList.remove("amber");
    });

    // Guest display names for popups.
    const names: Record<string, string> = {};
    (await getDocs(collection(db, "guests"))).forEach((d) => {
      names[d.id] = d.data().displayName ?? d.id.slice(0, 6);
    });

    // Cache sites (admin-only secrets hold the coordinates).
    const [cacheSecretSnap, cacheSnap] = await Promise.all([
      getDocs(collection(db, "cacheSecrets")),
      getDocs(collection(db, "caches")),
    ]);
    const cacheNames: Record<string, string> = {};
    cacheSnap.forEach((d) => (cacheNames[d.id] = d.data().name ?? d.id));
    cacheSecretSnap.forEach((d) => {
      const s = d.data();
      if (typeof s.lat === "number" && typeof s.lng === "number") {
        const isSupply = cacheSnap.docs.find((c) => c.id === d.id)?.data().kind === "powerup";
        const svg = isSupply
          ? `<span style="color:var(--green)">${SUPPLY_SVG}</span>`
          : `<span style="color:var(--amber)">${GUN_SVG}</span>`;
        sm.L.marker([s.lat, s.lng], { icon: pinIcon(sm.L, svg) })
          .addTo(sm.map)
          .bindPopup(`CACHE ${cacheNames[d.id] ?? d.id}`);
      }
    });

    // Live guest positions.
    const posLayer = sm.L.layerGroup().addTo(sm.map);
    onSnapshot(collection(db, "positions"), (snap) => {
      posLayer.clearLayers();
      snap.forEach((d) => {
        const p = d.data();
        if (typeof p.lat !== "number") return;
        const ageMin = p.updatedAt?.toMillis
          ? Math.round((Date.now() - p.updatedAt.toMillis()) / 60000)
          : null;
        // Drop positions older than 30 min entirely — a guest who left hours ago
        // shouldn't linger on the ops map. 10–30 min still shows, just dimmed.
        if (ageMin !== null && ageMin > 30) return;
        const stale = ageMin !== null && ageMin > 10;
        const m = sm.L.marker([p.lat, p.lng], {
          icon: blipIcon(sm.L, "var(--current)"),
          opacity: stale ? 0.4 : 1,
        }).addTo(posLayer);
        m.bindPopup(`${p.codename ?? names[d.id] ?? d.id} · ${ageMin ?? "?"}m ago`);
      });
    });
  })();

  return box;
}

// ---- recent selfie proofs + agent joins ----
async function proofsSection(): Promise<HTMLElement> {
  const box = el("div.card", {}, el("span.card__tag", {}, "FEED — PROOFS & JOINS"));
  const [photoSnap, guestSnap] = await Promise.all([
    getDocs(query(collection(db, "photos"), orderBy("createdAt", "desc"))),
    getDocs(collection(db, "guests")),
  ]);

  type FeedEntry = { ts: number; el: HTMLElement };
  const entries: FeedEntry[] = [];

  // Photo proofs
  photoSnap.docs.slice(0, 18).forEach((d) => {
    const p = d.data();
    if (!p.downloadURL) return;
    const wrap = el("div", { style: "position:relative" },
      el("img", { src: p.downloadURL, style: "width:100%;aspect-ratio:1;object-fit:cover;border-radius:6px;border:1px solid var(--line)" }),
    );
    const del = el("button", {
      style: "position:absolute;top:2px;right:2px;background:rgba(11,14,17,.8);color:var(--alert);border:1px solid var(--alert);border-radius:4px;font-size:11px;padding:2px 6px",
      title: "Delete proof",
    }, "✕") as HTMLButtonElement;
    del.addEventListener("click", async () => {
      if (!confirm("Delete this proof for everyone?")) return;
      del.disabled = true;
      try {
        await deleteDoc(doc(db, "photos", d.id));
        if (p.storagePath) {
          try { await deleteObject(ref(storage, p.storagePath)); } catch { /* orphan ok */ }
        }
        wrap.remove(); toast("Proof deleted.", "ok");
      } catch (e: unknown) {
        toast((e as { message?: string })?.message ?? "Delete failed.", "err");
        del.disabled = false;
      }
    });
    wrap.append(del);
    entries.push({ ts: p.createdAt?.toMillis?.() ?? 0, el: wrap });
  });

  // Agent join events: show avatar (or silhouette) + "JOINED" label
  await Promise.allSettled(guestSnap.docs.map(async (d) => {
    const g = d.data();
    const ts: number = g.createdAt?.toMillis?.() ?? 0;
    const avatarInner = el("div", { style: "width:100%;height:100%;position:relative;background:#0a0d10;display:flex;align-items:center;justify-content:center" });
    avatarInner.innerHTML = spySilhouette;
    if (g.avatarStatus === "ready" && g.avatarPath) {
      try {
        const url = await getDownloadURL(ref(storage, g.avatarPath as string));
        avatarInner.innerHTML = "";
        avatarInner.append(el("img", { src: url, style: "width:100%;height:100%;object-fit:cover;filter:grayscale(0.5) contrast(1.1);position:absolute;inset:0" }));
      } catch { /* keep silhouette */ }
    }
    avatarInner.append(el("div", {
      style: "position:absolute;bottom:0;left:0;right:0;background:linear-gradient(transparent,rgba(0,0,0,.88));padding:12px 4px 4px",
    },
      el("div", { style: "font-size:7px;font-family:var(--mono);letter-spacing:.1em;text-transform:uppercase;color:var(--green);text-align:center" }, "AGENT JOINED"),
      el("div", { style: "font-size:8px;font-family:var(--mono);color:var(--cream);text-align:center;margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis" }, String(g.displayName ?? d.id)),
    ));
    const card = el("div", { style: "aspect-ratio:1;overflow:hidden;border-radius:6px;border:1px solid var(--green);position:relative" }, avatarInner);
    entries.push({ ts, el: card });
  }));

  if (entries.length === 0) {
    box.append(el("p.tiny.dim", {}, "No agents or proofs yet."));
    return box;
  }

  entries.sort((a, b) => b.ts - a.ts);
  const grid = el("div", { style: "display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px" });
  entries.forEach((e) => grid.append(e.el));
  box.append(grid);
  return box;
}

// ---- master switches ----
function switchesSection(): HTMLElement {
  const box = el("div.card", {}, el("span.card__tag", {}, "MASTER SWITCHES"));
  const flags: Array<[string, string]> = [
    ["fullHuntUnlocked", "Full hunt unlocked (open caches)"],
    ["countdownEnabled", "Countdown visible"],
  ];
  const rowsWrap = el("div");
  box.append(rowsWrap);

  onSnapshot(doc(db, "settings/app"), (s) => {
    const data = s.data() ?? {};
    rowsWrap.replaceChildren(
      ...flags.map(([key, label]) => {
        const cb = el("input", { type: "checkbox" }) as HTMLInputElement;
        cb.checked = data[key] === true;
        cb.addEventListener("change", async () => {
          await updateDoc(doc(db, "settings/app"), { [key]: cb.checked });
          toast(`${label.split(" ")[0]} → ${cb.checked}`, "ok");
        });
        const toggle = el("label.toggle", {}, cb, el("span.toggle__track", {}));
        return el("div.row", { style: "gap:12px;padding:10px 0;align-items:center;border-bottom:1px solid var(--line)" },
          toggle, el("span.tiny", {}, label));
      }),
    );
  });

  // ---- help text (markdown) ----
  const helpTextarea = el("textarea.input", {
    placeholder: "## MISSION BRIEFING\n\nWrite your help text here. Supports **bold**, ## headings, and - list items.",
    rows: "8",
    style: "width:100%;margin-top:4px;font-family:var(--mono);font-size:12px",
  }) as HTMLTextAreaElement;
  const saveHelpBtn = el("button.btn", { style: "margin-top:8px" }, "Save help text") as HTMLButtonElement;
  saveHelpBtn.addEventListener("click", async () => {
    saveHelpBtn.disabled = true;
    try {
      await setDoc(doc(db, "settings/app"), { helpMarkdown: helpTextarea.value }, { merge: true });
      toast("Help text saved.", "ok");
    } catch (e: unknown) {
      toast((e as { message?: string })?.message ?? "Save failed.", "err");
    } finally { saveHelpBtn.disabled = false; }
  });
  onSnapshot(doc(db, "settings/app"), (s) => {
    const md = s.data()?.helpMarkdown as string | undefined;
    if (md != null && helpTextarea !== document.activeElement) helpTextarea.value = md;
  });
  box.append(el("div", { style: "margin-top:20px;border-top:1px solid var(--line);padding-top:16px" },
    el("span.card__tag", {}, "HELP TEXT (MARKDOWN)"),
    helpTextarea,
    saveHelpBtn,
  ));

  return box;
}

// ---- broadcasts ----
function broadcastSection(): HTMLElement {
  const title = el("input.input", { placeholder: "Title (e.g. CONTROL)" }) as HTMLInputElement;
  const body = el("textarea.input", { placeholder: "Message…", rows: "3" }) as HTMLTextAreaElement;
  const audience = el("select.input", {},
    el("option", { value: "all" }, "All agents"),
  ) as HTMLSelectElement;
  const type = el("select.input", {},
    ...["admin", "milestone", "countdown"].map((t) => el("option", { value: t }, t)),
  ) as HTMLSelectElement;
  const btn = el("button.btn", {}, "Transmit") as HTMLButtonElement;
  btn.addEventListener("click", async () => {
    if (!title.value && !body.value) return;
    btn.disabled = true;
    try {
      const r = await sendBroadcast({ title: title.value, body: body.value, type: type.value, audience: audience.value });
      toast(`Sent. Push delivered to ${r.pushed}.`, "ok");
      title.value = ""; body.value = "";
    } catch (e: unknown) { toast((e as { message?: string })?.message ?? "Failed", "err"); }
    finally { btn.disabled = false; }
  });
  return el("div.card", {},
    el("span.card__tag", {}, "BROADCAST"),
    el("div.field", {}, title),
    el("div.field", {}, body),
    el("div.grid2", {}, audience, type),
    el("div", { style: "margin-top:10px" }, btn),
  );
}

// ---- agents: photo grid + modal editor ----
async function guestsSection(): Promise<HTMLElement> {
  const box = el("div.card", {}, el("span.card__tag", {}, "AGENTS"));
  const snap = await getDocs(collection(db, "guests"));

  // Resolve avatar download URLs concurrently (best-effort).
  const avatarUrls = new Map<string, string>();
  await Promise.allSettled(snap.docs.map(async (d) => {
    const g = d.data();
    if (g.avatarStatus === "ready" && g.avatarPath) {
      try {
        const url = await getDownloadURL(ref(storage, g.avatarPath as string));
        avatarUrls.set(d.id, url);
      } catch { /* no avatar */ }
    }
  }));

  // ---- modal overlay ----
  const modalBackdrop = el("div", {
    style: "display:none;position:fixed;inset:0;z-index:2000;background:rgba(4,6,9,.88);backdrop-filter:blur(5px);align-items:center;justify-content:center;padding:16px",
  });
  const modalPanel = el("div", {
    style: "width:100%;max-width:460px;max-height:90vh;overflow-y:auto;background:var(--bg-2);border:1px solid var(--amber);border-radius:8px;padding:20px;position:relative",
  });
  modalBackdrop.append(modalPanel);
  modalBackdrop.addEventListener("click", (e) => { if (e.target === modalBackdrop) closeAgentModal(); });
  document.body.append(modalBackdrop);

  function closeAgentModal() { modalBackdrop.style.display = "none"; modalPanel.replaceChildren(); }
  function openAgentModal(id: string, g: Record<string, unknown>) {
    const closeBtn = el("button.btn--ghost.btn.btn--sm", {
      style: "position:absolute;top:12px;right:12px;min-width:32px;min-height:32px",
      onclick: closeAgentModal,
    }, "✕");
    modalPanel.replaceChildren(closeBtn, buildEditor(id, g));
    modalBackdrop.style.display = "flex";
  }

  // ---- editor form ----
  function buildEditor(id: string, g: Record<string, unknown>): HTMLElement {
    const nameI = el("input.input", {}) as HTMLInputElement;
    nameI.value = (g.displayName as string) ?? "";
    const realI = el("input.input", {}) as HTMLInputElement;
    realI.value = (g.realName as string) ?? "";
    const codeI = el("input.input", { style: "text-transform:uppercase" }) as HTMLInputElement;
    codeI.value = (g.code as string) ?? "";
    const roleI = el("select.input", {},
      el("option", { value: "guest" }, "guest"),
      el("option", { value: "admin" }, "admin"),
    ) as HTMLSelectElement;
    roleI.value = (g.role as string) ?? "guest";
    const intelI = el("input.input", { type: "number" }) as HTMLInputElement;
    intelI.value = String(g.totalIntel ?? 0);

    const save = el("button.btn--ghost.btn.btn--sm", { style: "font-size:11px" }, "Save") as HTMLButtonElement;
    save.addEventListener("click", async () => {
      save.disabled = true;
      try {
        const intel = Number(intelI.value) || 0;
        await updateDoc(doc(db, "guests", id), {
          displayName: nameI.value.trim(),
          realName: realI.value.trim(),
          code: codeI.value.trim().toUpperCase(),
          role: roleI.value,
          intelPoints: intel,
          totalIntel: intel,
        });
        toast("Agent saved.", "ok");
      } catch (e: unknown) {
        toast((e as { message?: string })?.message ?? "Failed", "err");
      } finally { save.disabled = false; }
    });

    const reset = el("button.btn--ghost.btn.btn--sm", { style: "font-size:11px" }, "Reset device");
    reset.addEventListener("click", async () => {
      try { await adminResetBinding({ guestId: id }); toast("Binding reset.", "ok"); }
      catch (e: unknown) { toast((e as { message?: string })?.message ?? "Failed", "err"); }
    });
    const del = el("button.btn--ghost.btn.btn--sm", { style: "font-size:11px;color:var(--alert)" }, "Delete");
    del.addEventListener("click", async () => {
      if (!confirm(`Delete agent ${g.displayName ?? id}? This cannot be undone.`)) return;
      try { await deleteDoc(doc(db, "guests", id)); closeAgentModal(); toast("Agent deleted. Reload.", "ok"); }
      catch (e: unknown) { toast((e as { message?: string })?.message ?? "Failed", "err"); }
    });

    const bound = g.boundDeviceId
      ? el("span.tiny.dim", {}, `Bound: ${String(g.boundDeviceId).slice(0, 14)}…`)
      : el("span.tiny.dim", {}, "No device bound");

    // ---- avatar section ----
    const avatarWrap = el("div", {
      style: "width:64px;height:64px;border-radius:50%;overflow:hidden;background:#0a0d10;border:1px solid var(--line);flex-shrink:0;display:flex;align-items:center;justify-content:center",
    });
    avatarWrap.innerHTML = spySilhouette;
    if ((g.avatarStatus as string) === "ready" && g.avatarPath) {
      getDownloadURL(ref(storage, g.avatarPath as string))
        .then((url) => {
          avatarWrap.innerHTML = "";
          avatarWrap.append(el("img", { src: url, style: "width:100%;height:100%;object-fit:cover;filter:grayscale(0.5) contrast(1.1)" }));
        }).catch(() => {});
    }
    const avatarStatusEl = el("span.tiny.dim", {},
      (g.avatarStatus as string) === "ready" ? "Cover identity active." :
      (g.avatarStatus as string) === "pending" ? "In the lab…" : "No cover identity.");
    const avatarFile = el("input", { type: "file", accept: "image/*", capture: "user", style: "display:none" }) as HTMLInputElement;
    const genBtn = el("button.btn.btn--ghost.btn--sm", { style: "font-size:11px" }, "📷 Generate avatar") as HTMLButtonElement;
    genBtn.addEventListener("click", () => avatarFile.click());
    avatarFile.addEventListener("change", async () => {
      const file = avatarFile.files?.[0]; if (!file) return;
      genBtn.disabled = true; avatarStatusEl.textContent = "Processing…";
      try {
        const adminUid = currentUser()!.uid;
        const compressed = await compressImage(file);
        const { path } = await uploadSelfie(adminUid, compressed, "avatars");
        try {
          const res = await generateAvatar({ sourcePath: path, targetGuestId: id });
          if (!res.ok) throw new Error("generation failed");
          await updateDoc(doc(db, "guests", id), { avatarPath: res.avatarPath, avatarStatus: "ready" });
          const url = await getDownloadURL(ref(storage, res.avatarPath));
          avatarWrap.innerHTML = "";
          avatarWrap.append(el("img", { src: url, style: "width:100%;height:100%;object-fit:cover;filter:grayscale(0.5) contrast(1.1)" }));
          avatarStatusEl.textContent = "Cover identity active.";
          toast("Spy avatar generated.", "ok");
        } catch {
          toast("Pipeline offline — field filter applied.", "warn");
          const blob = await surveillanceFilter(file);
          const destPath = `avatars/${id}/spy.jpg`;
          await uploadBytes(ref(storage, destPath), blob, { contentType: "image/jpeg" });
          const url2 = await getDownloadURL(ref(storage, destPath));
          await updateDoc(doc(db, "guests", id), { avatarPath: destPath, avatarStatus: "ready" });
          avatarWrap.innerHTML = "";
          avatarWrap.append(el("img", { src: url2, style: "width:100%;height:100%;object-fit:cover;filter:grayscale(0.5) contrast(1.1)" }));
          avatarStatusEl.textContent = "Cover identity active (field filter).";
        }
      } catch (e: unknown) {
        toast((e as { message?: string })?.message ?? "Avatar failed.", "err");
        avatarStatusEl.textContent = "Generation failed.";
      } finally { genBtn.disabled = false; avatarFile.value = ""; }
    });

    return el("div", { style: "padding-top:4px" },
      el("div.row", { style: "gap:12px;align-items:center;margin-bottom:12px" },
        avatarWrap,
        el("div", {},
          el("span.mono.tiny.amber", {}, String(g.displayName ?? id)),
          el("div", { style: "margin-top:4px" }, avatarStatusEl),
          el("div.row", { style: "gap:6px;margin-top:6px;flex-wrap:wrap;align-items:center" }, genBtn, avatarFile, bound),
        ),
      ),
      el("div.grid2", {}, labeled("Codename", nameI), labeled("Real name", realI)),
      el("div.grid2", {}, labeled("Entry code", codeI), labeled("Role", roleI)),
      el("div.grid2", {}, labeled("Intel ◆", intelI)),
      el("div.row", { style: "gap:6px;flex-wrap:wrap;margin-top:8px" }, save, reset, del),
    );
  }

  // ---- photo grid ----
  const grid = el("div", { style: "display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px" });

  snap.docs.forEach((d) => {
    const g = d.data();
    const avatarUrl = avatarUrls.get(d.id);

    const imgWrap = el("div", { style: "aspect-ratio:1;overflow:hidden;position:relative;background:#0a0d10" });
    if (avatarUrl) {
      imgWrap.append(el("img", { src: avatarUrl, style: "width:100%;height:100%;object-fit:cover;filter:grayscale(0.5) contrast(1.1)" }));
    } else {
      imgWrap.innerHTML = spySilhouette;
    }

    // Codename badge
    imgWrap.append(el("div", {
      style: "position:absolute;bottom:0;left:0;right:0;background:linear-gradient(transparent,rgba(0,0,0,.88));padding:10px 5px 3px;font-size:8px;font-family:var(--mono);letter-spacing:.1em;text-transform:uppercase;color:var(--amber)",
    }, String(g.displayName ?? "—")));

    // Edit button: hidden by default, shown on hover via JS (works on desktop + touch tap)
    const editBtn = el("button", {
      style: "position:absolute;top:5px;left:5px;padding:3px 8px;font-size:9px;font-family:var(--mono);letter-spacing:.08em;background:rgba(0,0,0,.75);color:var(--amber);border:1px solid var(--amber);border-radius:3px;cursor:pointer;opacity:0;transition:opacity .15s",
    }, "✎ EDIT") as HTMLButtonElement;
    imgWrap.append(editBtn);

    const intelEl = el("div", { style: "font-size:9px;color:var(--dim);text-align:center;padding:3px 2px;font-family:var(--mono)" },
      `${g.totalIntel ?? 0}◆`);

    const card = el("div", {
      style: "border:1px solid var(--line);border-radius:6px;overflow:hidden;background:var(--bg-2);cursor:pointer;transition:border-color .15s",
    }, imgWrap, intelEl);

    // Hover: show edit button + highlight border
    card.addEventListener("mouseenter", () => {
      editBtn.style.opacity = "1";
      card.style.borderColor = "var(--amber)";
    });
    card.addEventListener("mouseleave", () => {
      editBtn.style.opacity = "0";
      card.style.borderColor = "var(--line)";
    });

    // Click anywhere on card (or edit btn) → open modal
    card.addEventListener("click", () => openAgentModal(d.id, d.data()));

    grid.append(card);
  });

  // ---- add agent modal ----
  function openAddAgentModal() {
    const nName = el("input.input", { placeholder: "Codename" }) as HTMLInputElement;
    const nReal = el("input.input", { placeholder: "Real name" }) as HTMLInputElement;
    const nCode = el("input.input", { placeholder: "Entry code (e.g. WOLF)", style: "text-transform:uppercase" }) as HTMLInputElement;
    const nRole = el("select.input", {},
      el("option", { value: "guest" }, "guest"),
      el("option", { value: "admin" }, "admin"),
    ) as HTMLSelectElement;
    const save = el("button.btn", {}, "Add agent") as HTMLButtonElement;
    save.addEventListener("click", async () => {
      if (!nName.value.trim() || !nCode.value.trim()) { toast("Codename + code required.", "warn"); return; }
      save.disabled = true;
      try {
        await setDoc(doc(collection(db, "guests")), {
          displayName: nName.value.trim(),
          realName: nReal.value.trim(),
          code: nCode.value.trim().toUpperCase(),
          role: nRole.value,
          intelPoints: 0, totalIntel: 0,
          pushToken: null, pushEnabled: false, shareLocation: false,
          avatarStatus: "none", avatarPath: null,
          boundDeviceId: null, boundAt: null,
          createdAt: serverTimestamp(),
        });
        toast("Agent added. Reload to see in grid.", "ok");
        closeAgentModal();
      } catch (e: unknown) {
        toast((e as { message?: string })?.message ?? "Failed", "err");
      } finally { save.disabled = false; }
    });
    const closeBtn = el("button.btn--ghost.btn.btn--sm", {
      style: "position:absolute;top:12px;right:12px;min-width:32px;min-height:32px",
      onclick: closeAgentModal,
    }, "✕");
    modalPanel.replaceChildren(closeBtn, el("div", { style: "padding-top:4px" },
      el("span.mono.tiny.amber", {}, "NEW AGENT"),
      el("div.grid2", {}, labeled("Codename", nName), labeled("Real name", nReal)),
      el("div.grid2", {}, labeled("Entry code", nCode), labeled("Role", nRole)),
      el("div", { style: "margin-top:10px" }, save),
    ));
    modalBackdrop.style.display = "flex";
  }

  const addBtn = el("button.btn.btn--ghost", { style: "font-size:13px;margin-top:4px" }, "+ Add agent") as HTMLButtonElement;
  addBtn.addEventListener("click", openAddAgentModal);

  box.append(grid, addBtn);
  return box;
}

// ---- shared editor helpers ----
function toLocalInputValue(ts?: { toMillis(): number } | null): string {
  if (!ts?.toMillis) return "";
  const d = new Date(ts.toMillis());
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInputValue(v: string): Timestamp | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
}
function csv(v: string): string[] {
  return v.split(",").map((s) => s.trim()).filter(Boolean);
}
function labeled(text: string, input: HTMLElement): HTMLElement {
  return el("div.field", {}, el("label.tiny", {}, text), input);
}


// ---- cache editor: grid + modal with inline map, all kinds ----
async function cachesSection(): Promise<HTMLElement> {
  const box = el("div.card", {}, el("span.card__tag", {}, "CACHES — HYDRO-ORDNANCE"));
  const [cSnap, secretSnap] = await Promise.all([
    getDocs(collection(db, "caches")),
    getDocs(collection(db, "cacheSecrets")),
  ]);
  const secrets: Record<string, Record<string, unknown>> = {};
  secretSnap.forEach((s) => (secrets[s.id] = s.data()));

  // ---- modal ----
  const modalBackdrop = el("div", {
    style: "display:none;position:fixed;inset:0;z-index:2000;background:rgba(4,6,9,.9);backdrop-filter:blur(5px);align-items:flex-start;justify-content:center;padding:20px;overflow-y:auto",
  });
  const modalPanel = el("div", {
    style: "width:100%;max-width:540px;background:var(--bg-2);border:1px solid var(--amber);border-radius:8px;padding:22px;position:relative;margin:auto",
  });
  modalBackdrop.append(modalPanel);
  modalBackdrop.addEventListener("click", (e) => { if (e.target === modalBackdrop) closeCacheModal(); });
  document.body.append(modalBackdrop);
  function closeCacheModal() { modalBackdrop.style.display = "none"; modalPanel.replaceChildren(); }

  async function openCacheModal(id: string | null, c: Record<string, unknown>, sec: Record<string, unknown>) {
    const isNew = id === null;
    const effectiveId = isNew ? doc(collection(db, "caches")).id : id!;

    const closeBtn = el("button.btn--ghost.btn.btn--sm", {
      style: "position:absolute;top:12px;right:12px;min-width:32px;min-height:32px",
      onclick: closeCacheModal,
    }, "✕");

    const nameI = el("input.input", { placeholder: "Cache name" }) as HTMLInputElement;
    nameI.value = (c.name as string) ?? "";
    const tierI = el("input.input", { type: "number", min: "1", max: "5", placeholder: "1–5" }) as HTMLInputElement;
    tierI.value = String(c.powerTier ?? 1);
    const kindI = el("select.input", {},
      el("option", { value: "gun" }, "Water gun ★"),
      el("option", { value: "powerup" }, "Supply drop (snacks/beers)"),
      el("option", { value: "party" }, "Party location (time reveal)"),
      el("option", { value: "dinner" }, "Dinner location (manual reveal)"),
    ) as HTMLSelectElement;
    kindI.value = (c.kind as string) ?? "gun";

    const riddle = el("textarea.input", { rows: "2", placeholder: "Riddle / clue" }) as HTMLTextAreaElement;
    riddle.value = (c.riddlePrompt as string) ?? "";
    const placeAnswers = el("input.input", { placeholder: "Place answers (comma-separated)" }) as HTMLInputElement;
    placeAnswers.value = ((sec.locationAnswers as string[]) ?? []).join(", ");
    const gunCodes = el("input.input", { placeholder: "Claim codes (comma-separated)" }) as HTMLInputElement;
    gunCodes.value = ((sec.acceptedAnswers as string[]) ?? []).join(", ");
    const revealCost = el("input.input", { type: "number", placeholder: "0" }) as HTMLInputElement;
    revealCost.value = String(sec.revealCost ?? c.revealCost ?? 0);
    const release = el("input.input", { type: "datetime-local" }) as HTMLInputElement;
    release.value = toLocalInputValue(c.releaseAt as { toMillis(): number } | null);
    const hints = el("textarea.input", { rows: "2", placeholder: "cost: hint text (one per line)" }) as HTMLTextAreaElement;
    hints.value = ((sec.hints as Array<{ cost: number; text: string }>) ?? [])
      .map((h) => `${h.cost}: ${h.text}`).join("\n");

    const lat = el("input.input", { type: "number", step: "any", placeholder: "lat" }) as HTMLInputElement;
    const lng = el("input.input", { type: "number", step: "any", placeholder: "lng" }) as HTMLInputElement;
    if (typeof sec.lat === "number") lat.value = String(sec.lat);
    if (typeof sec.lng === "number") lng.value = String(sec.lng);

    const currentRadius = typeof c.roughRadius === "number" ? c.roughRadius : 50;
    const radiusVal = el("span.mono.tiny.amber", {}, `${currentRadius} m`);
    const radiusSlider = el("input", {
      type: "range", min: "10", max: "500", step: "5",
      value: String(currentRadius), style: "width:100%;accent-color:var(--amber)",
    }) as HTMLInputElement;
    radiusSlider.addEventListener("input", () => { radiusVal.textContent = `${radiusSlider.value} m`; });

    let locationPhotoUrl = (c.locationPhotoUrl as string) ?? "";
    const locPhotoPreview = el("img", {
      src: locationPhotoUrl,
      style: `width:100%;border-radius:6px;margin:4px 0;filter:grayscale(0.5) contrast(1.1);${locationPhotoUrl ? "" : "display:none"}`,
    }) as HTMLImageElement;
    const locPhotoInput = el("input", { type: "file", accept: "image/*", capture: "environment" }) as HTMLInputElement;
    locPhotoInput.addEventListener("change", async () => {
      const f = locPhotoInput.files?.[0]; if (!f) return;
      try {
        const blob = await compressImage(f);
        const r = ref(storage, `cache-locations/${effectiveId}.jpg`);
        await uploadBytes(r, blob, { contentType: "image/jpeg" });
        locationPhotoUrl = await getDownloadURL(r);
        locPhotoPreview.src = locationPhotoUrl; locPhotoPreview.style.display = "";
        toast("Location photo uploaded.", "ok");
      } catch (e: unknown) { toast((e as { message?: string })?.message ?? "Upload failed", "err"); }
    });

    // Inline Leaflet map for coord picking
    const mapContainer = el("div", { style: "height:220px;border-radius:6px;overflow:hidden;border:1px solid var(--line);margin-bottom:4px" });
    const coordHint = el("span.tiny.dim", {}, lat.value && lng.value ? `📍 ${lat.value}, ${lng.value}` : "Tap the map to set location");

    const save = el("button.btn.btn--sm", {}, isNew ? "Create cache" : "Save") as HTMLButtonElement;
    save.addEventListener("click", async () => {
      if (isNew && !nameI.value.trim()) { toast("Cache name required.", "warn"); return; }
      save.disabled = true;
      try {
        const parsedHints = hints.value.split("\n").map((line) => {
          const m = line.match(/^\s*(\d+)\s*:\s*(.+)$/);
          return m ? { cost: Number(m[1]), text: m[2].trim() } : null;
        }).filter((h): h is { cost: number; text: string } => h !== null);
        const rel = fromLocalInputValue(release.value);
        await setDoc(doc(db, "caches", effectiveId), {
          name: nameI.value.trim() || effectiveId,
          kind: kindI.value,
          powerTier: Number(tierI.value) || 1,
          riddlePrompt: riddle.value,
          revealCost: Number(revealCost.value) || 0,
          hintCost: parsedHints[0]?.cost ?? 0,
          locationPhotoUrl: locationPhotoUrl || null,
          ...(rel ? { releaseAt: rel } : {}),
          ...(lat.value && lng.value
            ? roughZoneFor(effectiveId, Number(lat.value), Number(lng.value), Number(radiusSlider.value))
            : {}),
        }, { merge: true });
        await setDoc(doc(db, "cacheSecrets", effectiveId), {
          locationAnswers: csv(placeAnswers.value),
          acceptedAnswers: csv(gunCodes.value),
          revealCost: Number(revealCost.value) || 0,
          hints: parsedHints,
          ...(lat.value && lng.value ? { lat: Number(lat.value), lng: Number(lng.value) } : {}),
        }, { merge: true });
        toast(isNew ? "Cache created. Reload to refresh." : "Saved.", "ok");
        if (isNew) closeCacheModal();
      } catch (e: unknown) {
        toast((e as { message?: string })?.message ?? "Save failed.", "err");
      } finally { save.disabled = false; }
    });

    const del = isNew ? null : el("button.btn--ghost.btn.btn--sm", { style: "color:var(--alert)" }, "Delete") as HTMLButtonElement;
    del?.addEventListener("click", async () => {
      if (!confirm(`Delete cache ${c.name ?? effectiveId}?`)) return;
      try {
        await deleteDoc(doc(db, "caches", effectiveId));
        await deleteDoc(doc(db, "cacheSecrets", effectiveId));
        closeCacheModal(); toast("Cache deleted. Reload to refresh.", "ok");
      } catch (e: unknown) { toast((e as { message?: string })?.message ?? "Failed", "err"); }
    });

    const form = el("div", { style: "padding-top:4px" },
      el("span.mono.tiny.amber", {}, isNew ? "NEW CACHE" : String(c.name ?? effectiveId)),
      labeled("Type", kindI),
      el("div.grid2", {}, labeled("Name", nameI), labeled("Power tier (guns only)", tierI)),
      labeled("Riddle / clue", riddle),
      labeled("Place answers (free reveal)", placeAnswers),
      labeled("Claim codes (write on the item!)", gunCodes),
      el("div.grid2", {}, labeled("Pin cost ◆", revealCost), labeled("Release time", release)),
      labeled("Hints (cost: text, one per line)", hints),
      el("div.field", {},
        el("label.tiny", {}, "Location — tap map to pin"),
        mapContainer,
        coordHint,
        el("div.grid2", { style: "margin-top:4px" }, labeled("Lat", lat), labeled("Lng", lng)),
      ),
      el("div.field", {},
        el("div.row", { style: "justify-content:space-between;margin-bottom:4px" },
          el("label.tiny", {}, "Rough zone radius"), radiusVal),
        radiusSlider,
        el("span.tiny.dim", {}, "Search circle radius shown to guests"),
      ),
      el("div.field", {},
        el("label.tiny", {}, "Location photo (shown after unlock)"),
        locPhotoPreview,
        locPhotoInput,
      ),
      el("div.row", { style: "gap:8px;margin-top:12px" }, save, ...(del ? [del] : [])),
    );

    modalPanel.replaceChildren(closeBtn, form);
    modalBackdrop.style.display = "flex";

    // Leaflet needs a visible container — init after backdrop is painted
    setTimeout(async () => {
      const initLat = lat.value ? Number(lat.value) : 48.229;
      const initLng = lng.value ? Number(lng.value) : 16.412;
      const sm = await spyMap(mapContainer, { center: [initLat, initLng], zoom: lat.value ? 17 : 14 });
      sm.map.invalidateSize();
      // Use a divIcon to avoid the broken default Leaflet marker PNG in Vite builds
      const pinDivIcon = () => sm.L.divIcon({
        className: "",
        html: `<div style="width:14px;height:14px;border-radius:50%;background:var(--amber);border:2px solid var(--cream);box-shadow:0 0 10px rgba(255,176,0,.7)"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });
      let marker: ReturnType<typeof sm.L.marker> | null = null;
      if (lat.value && lng.value) {
        marker = sm.L.marker([initLat, initLng], { icon: pinDivIcon() }).addTo(sm.map);
      }
      sm.map.on("click", (e) => {
        const { lat: la, lng: ln } = (e as { latlng: { lat: number; lng: number } }).latlng;
        lat.value = la.toFixed(6); lng.value = ln.toFixed(6);
        coordHint.textContent = `📍 ${lat.value}, ${lng.value}`;
        marker?.remove();
        marker = sm.L.marker([la, ln], { icon: pinDivIcon() }).addTo(sm.map);
      });
    }, 60);
  }

  // ---- grid helpers ----
  function kindIcon(kind: string): string {
    if (kind === "powerup") return `<span style="color:var(--green);display:flex;align-items:center">${SUPPLY_SVG}</span>`;
    if (kind === "party") return `<span style="font-size:22px">🎉</span>`;
    if (kind === "dinner") return `<span style="font-size:22px">🍽️</span>`;
    return `<span style="color:var(--amber);display:flex;align-items:center">${GUN_SVG}</span>`;
  }
  function kindAccent(kind: string): string {
    if (kind === "powerup") return "var(--green)";
    if (kind === "party") return "#c87eff";
    if (kind === "dinner") return "var(--cream)";
    return "var(--amber)";
  }

  const grid = el("div", { style: "display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px" });
  const kindOrder: Record<string, number> = { gun: 0, powerup: 1, party: 2, dinner: 3 };
  const sorted = [...cSnap.docs].sort((a, b) => {
    const ka = kindOrder[(a.data().kind as string) ?? "gun"] ?? 0;
    const kb = kindOrder[(b.data().kind as string) ?? "gun"] ?? 0;
    return ka !== kb ? ka - kb : ((a.data().powerTier as number) ?? 0) - ((b.data().powerTier as number) ?? 0);
  });

  sorted.forEach((d) => {
    const c = d.data();
    const kind = (c.kind as string) ?? "gun";
    const sec = secrets[d.id] ?? {};
    const color = kindAccent(kind);

    const locPhoto = (c.locationPhotoUrl as string) ?? "";
    const bgStyle = locPhoto
      ? `background:linear-gradient(rgba(4,6,9,.60),rgba(4,6,9,.60)),url(${locPhoto}) center/cover no-repeat`
      : "background:#0a0d10";
    const inner = el("div", {
      style: `aspect-ratio:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;${bgStyle};position:relative;padding:8px;box-sizing:border-box`,
    });

    const iconWrap = el("div", { style: "display:flex;align-items:center;justify-content:center;height:36px" });
    iconWrap.innerHTML = kindIcon(kind);
    inner.append(iconWrap);

    inner.append(el("div", {
      style: `font-size:8px;font-family:var(--mono);letter-spacing:.08em;text-transform:uppercase;color:${color};text-align:center;line-height:1.3;word-break:break-word`,
    }, String(c.name ?? d.id)));

    if (kind === "gun") {
      const tier = (c.powerTier as number) ?? 1;
      const dots = el("div", { style: "display:flex;gap:2px" });
      for (let i = 0; i < 5; i++) {
        dots.append(el("span", {
          style: `display:inline-block;width:4px;height:4px;border-radius:50%;background:${i < tier ? "var(--amber)" : "var(--line)"}`,
        }));
      }
      inner.append(dots);
    }

    if (typeof sec.lat === "number") {
      inner.append(el("div", { style: "position:absolute;top:5px;right:5px;font-size:9px;color:var(--green)" }, "📍"));
    }

    const editBtn = el("button", {
      style: "position:absolute;top:5px;left:5px;padding:3px 8px;font-size:9px;font-family:var(--mono);letter-spacing:.08em;background:rgba(0,0,0,.75);color:var(--amber);border:1px solid var(--amber);border-radius:3px;cursor:pointer;opacity:0;transition:opacity .15s",
    }, "✎ EDIT") as HTMLButtonElement;
    inner.append(editBtn);

    const card = el("div", { style: "border:1px solid var(--line);border-radius:6px;overflow:hidden;cursor:pointer;transition:border-color .15s" }, inner);
    card.addEventListener("mouseenter", () => { editBtn.style.opacity = "1"; card.style.borderColor = color; });
    card.addEventListener("mouseleave", () => { editBtn.style.opacity = "0"; card.style.borderColor = "var(--line)"; });
    card.addEventListener("click", () => void openCacheModal(d.id, c, sec));
    grid.append(card);
  });

  const addBtn = el("button.btn.btn--ghost", { style: "font-size:13px;margin-top:4px" }, "+ Add cache") as HTMLButtonElement;
  addBtn.addEventListener("click", () => void openCacheModal(null, {}, {}));

  box.append(grid, addBtn);
  return box;
}

// ---- dossier cases: photo grid + modal editor ----
async function uploadCaseImage(file: File): Promise<string> {
  const blob = await compressImage(file);
  const r = ref(storage, `cases/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`);
  await uploadBytes(r, blob, { contentType: "image/jpeg" });
  return getDownloadURL(r);
}

async function casesSection(): Promise<HTMLElement> {
  const box = el("div.card", {}, el("span.card__tag", {}, "CASE FILES"));
  const [caseSnap, secretSnap, guestSnap] = await Promise.all([
    getDocs(collection(db, "cases")),
    getDocs(collection(db, "caseSecrets")),
    getDocs(collection(db, "guests")),
  ]);
  const secrets: Record<string, { answers: string[]; reward: number }> = {};
  secretSnap.forEach((s) => (secrets[s.id] = {
    answers: s.data().acceptedAnswers ?? [],
    reward: s.data().intelReward ?? 75,
  }));

  const guestNames: Record<string, string> = {};
  guestSnap.docs.forEach((d) => { guestNames[d.id] = d.data().displayName as string; });
  const guestOpts = () => [
    el("option", { value: "" }, "— no guest (standalone) —"),
    ...guestSnap.docs
      .filter((d) => d.data().role === "guest")
      .map((d) => el("option", { value: d.id }, d.data().displayName as string)),
  ];

  // ---- shared modal ----
  const modalBackdrop = el("div", {
    style: "display:none;position:fixed;inset:0;z-index:2000;background:rgba(4,6,9,.9);backdrop-filter:blur(5px);align-items:flex-start;justify-content:center;padding:20px;overflow-y:auto",
  });
  const modalPanel = el("div", {
    style: "width:100%;max-width:520px;background:var(--bg-2);border:1px solid var(--amber);border-radius:8px;padding:22px;position:relative;margin:auto",
  });
  modalBackdrop.append(modalPanel);
  modalBackdrop.addEventListener("click", (e) => { if (e.target === modalBackdrop) closeCaseModal(); });
  document.body.append(modalBackdrop);

  function closeCaseModal() { modalBackdrop.style.display = "none"; modalPanel.replaceChildren(); }
  function openCaseModal(content: HTMLElement) {
    const closeBtn = el("button.btn--ghost.btn.btn--sm", {
      style: "position:absolute;top:12px;right:12px;min-width:32px;min-height:32px",
      onclick: closeCaseModal,
    }, "✕");
    modalPanel.replaceChildren(closeBtn, content);
    modalBackdrop.style.display = "flex";
  }

  // ---- editor form ----
  function buildCaseEditor(id: string, c: Record<string, unknown>, answers: string[], reward: number, onSaved?: () => void): HTMLElement {
    const titleI = el("input.input", {}) as HTMLInputElement;
    titleI.value = (c.title as string) ?? "";
    const promptI = el("textarea.input", { rows: "3" }) as HTMLTextAreaElement;
    promptI.value = (c.riddlePrompt as string) ?? "";
    const ansI = el("input.input", { placeholder: "accepted answers, comma-separated" }) as HTMLInputElement;
    ansI.value = answers.join(", ");
    const rewardI = el("input.input", { type: "number" }) as HTMLInputElement;
    rewardI.value = String(reward);
    const subjectI = el("select.input", {}, ...guestOpts()) as HTMLSelectElement;
    subjectI.value = (c.subjectGuestId as string) ?? "";

    let imageUrl = (c.imageUrl as string) ?? "";
    const preview = el("img", {
      src: imageUrl,
      style: `width:100%;border-radius:6px;margin:6px 0;filter:grayscale(0.8) contrast(1.15) sepia(0.2);${imageUrl ? "" : "display:none"}`,
    }) as HTMLImageElement;
    const fileI = el("input", { type: "file", accept: "image/*" }) as HTMLInputElement;
    fileI.addEventListener("change", async () => {
      const f = fileI.files?.[0]; if (!f) return;
      try {
        imageUrl = await uploadCaseImage(f);
        preview.src = imageUrl; preview.style.display = "";
        toast("Image uploaded — Save to attach.", "ok");
      } catch (e: unknown) { toast((e as { message?: string })?.message ?? "Upload failed", "err"); }
    });
    const clearImg = el("button.btn--ghost.btn.btn--sm", { style: "font-size:10px" }, "Remove image");
    clearImg.addEventListener("click", () => { imageUrl = ""; preview.style.display = "none"; preview.src = ""; });

    const save = el("button.btn.btn--sm", { style: "margin-top:4px" }, "Save") as HTMLButtonElement;
    save.addEventListener("click", async () => {
      save.disabled = true;
      try {
        await setDoc(doc(db, "cases", id), {
          title: titleI.value || "Case File",
          riddlePrompt: promptI.value,
          subjectGuestId: subjectI.value || null,
          imageUrl: imageUrl || null,
        }, { merge: true });
        await setDoc(doc(db, "caseSecrets", id), {
          acceptedAnswers: csv(ansI.value),
          intelReward: Number(rewardI.value) || 0,
        }, { merge: true });
        toast("Case saved.", "ok");
        onSaved?.();
      } catch (e: unknown) {
        toast((e as { message?: string })?.message ?? "Save failed.", "err");
      } finally { save.disabled = false; }
    });
    const del = el("button.btn--ghost.btn.btn--sm", { style: "font-size:11px;color:var(--alert)" }, "Delete");
    del.addEventListener("click", async () => {
      if (!confirm("Delete this case?")) return;
      try {
        await deleteDoc(doc(db, "cases", id));
        await deleteDoc(doc(db, "caseSecrets", id));
        closeCaseModal();
        toast("Case deleted. Reload to refresh grid.", "ok");
      } catch (e: unknown) { toast((e as { message?: string })?.message ?? "Failed", "err"); }
    });

    return el("div", {},
      el("div.row", { style: "justify-content:space-between;align-items:center;margin-bottom:12px" },
        el("span.mono.tiny.amber", {}, String(c.title || id)),
        el("div.row", { style: "gap:8px;align-items:center" },
          releaseToggle("cases", id, c.released === true),
          countdown(c.releaseAt as { toMillis(): number } | undefined),
        ),
      ),
      preview,
      el("div.field", { style: "margin-bottom:6px" },
        el("label.tiny", {}, "Image"), fileI, clearImg),
      labeled("Title", titleI),
      labeled("Question / clue", promptI),
      labeled("Accepted answers", ansI),
      el("div.grid2", {}, labeled("Intel reward ◆", rewardI), labeled("Profiles guest", subjectI)),
      el("div.row", { style: "gap:8px;margin-top:10px" }, save, del),
    );
  }

  // ---- photo grid ----
  const noImageSvg = `<svg viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%;padding:10px;opacity:0.2"><rect x="4" y="4" width="72" height="52" rx="3" stroke="var(--amber)" stroke-width="2"/><circle cx="30" cy="24" r="8" stroke="var(--amber)" stroke-width="2"/><path d="M4 44l20-14 14 10 10-8 28 18" stroke="var(--amber)" stroke-width="2"/></svg>`;

  const cases = caseSnap.docs.map((d) => ({ id: d.id, data: d.data() }));
  cases.sort((a, b) => ((a.data.title as string) ?? "").localeCompare((b.data.title as string) ?? ""));

  const grid = el("div", { style: "display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px" });

  cases.forEach(({ id, data: c }) => {
    const imageUrl = c.imageUrl as string | null;
    const isLive = c.released === true;
    const subjectName = c.subjectGuestId ? (guestNames[c.subjectGuestId as string] ?? "") : "";

    const imgWrap = el("div", { style: "aspect-ratio:3/4;overflow:hidden;position:relative;background:#0a0d10" });
    if (imageUrl) {
      imgWrap.append(el("img", {
        src: imageUrl,
        style: "width:100%;height:100%;object-fit:cover;filter:grayscale(0.7) contrast(1.1) sepia(0.15)",
      }));
    } else {
      imgWrap.innerHTML = noImageSvg;
    }

    // Title + subject at bottom
    imgWrap.append(el("div", {
      style: "position:absolute;bottom:0;left:0;right:0;background:linear-gradient(transparent,rgba(0,0,0,.92));padding:18px 7px 5px",
    },
      el("div", { style: "font-size:8px;font-family:var(--mono);letter-spacing:.1em;text-transform:uppercase;color:var(--amber);line-height:1.3" },
        String(c.title ?? "Untitled")),
      subjectName ? el("div", { style: "font-size:7px;font-family:var(--mono);color:var(--dim);margin-top:1px" }, subjectName) : el("span"),
    ));

    // LIVE badge
    if (isLive) {
      imgWrap.append(el("div", {
        style: "position:absolute;top:5px;right:5px;font-size:7px;font-family:var(--mono);letter-spacing:.1em;padding:2px 5px;background:var(--green);color:#000;border-radius:3px",
      }, "LIVE"));
    }

    // ✎ EDIT button on hover
    const editBtn = el("button", {
      style: "position:absolute;top:5px;left:5px;padding:3px 8px;font-size:9px;font-family:var(--mono);letter-spacing:.08em;background:rgba(0,0,0,.75);color:var(--amber);border:1px solid var(--amber);border-radius:3px;cursor:pointer;opacity:0;transition:opacity .15s",
    }, "✎ EDIT") as HTMLButtonElement;
    imgWrap.append(editBtn);

    const card = el("div", {
      style: "border:1px solid var(--line);border-radius:6px;overflow:hidden;background:var(--bg-2);cursor:pointer;transition:border-color .15s",
    }, imgWrap);

    card.addEventListener("mouseenter", () => { editBtn.style.opacity = "1"; card.style.borderColor = "var(--amber)"; });
    card.addEventListener("mouseleave", () => { editBtn.style.opacity = "0"; card.style.borderColor = "var(--line)"; });
    card.addEventListener("click", () =>
      openCaseModal(buildCaseEditor(id, c, secrets[id]?.answers ?? [], secrets[id]?.reward ?? 75)));

    grid.append(card);
  });

  // ---- add case button (opens modal) ----
  const addCaseBtn = el("button.btn.btn--ghost", { style: "font-size:13px;margin-top:4px" }, "+ Add case") as HTMLButtonElement;
  addCaseBtn.addEventListener("click", () => {
    const newId = doc(collection(db, "cases")).id;
    openCaseModal(buildCaseEditor(newId, { title: "" }, [], 75, closeCaseModal));
  });

  box.append(grid, addCaseBtn);
  return box;
}
